import { Injectable } from '@angular/core';
import { Firestore, collection, addDoc, query, where, collectionData, doc, updateDoc, deleteDoc, getDoc } from '@angular/fire/firestore';
import { Observable, combineLatest, from, map, switchMap } from 'rxjs';
import { orderBy, Timestamp } from 'firebase/firestore';
import { Application } from '../../models/application';
import { ApplicationResponse } from '../../models/applicationResponse';

@Injectable({
  providedIn: 'root'
})
export class ApplicationService {
  private applicationsCollection;

  constructor(private firestore: Firestore) {
    this.applicationsCollection = collection(this.firestore, 'applications');
  }

  // Submit a new application
  createApplication(request: Application): Observable<string> {
    request.applicationDate = Timestamp.now();
    return new Observable(observer => {
      addDoc(this.applicationsCollection, request)
        .then(docRef => {
          observer.next(docRef.id);
          observer.complete();
        })
        .catch(err => observer.error(err));
    });
  }

  // Get all applications by user with user and job details
  getApplicationsByUser(userId: string): Observable<ApplicationResponse[]> {
  // Apply where and orderBy to the Firestore query
  const q = query(this.applicationsCollection, where('userId', '==', userId), orderBy('applicationDate', 'desc'));

  return (collectionData(q, { idField: 'id' }) as Observable<Application[]>).pipe(
    switchMap((applications: Application[]) => {
      const detailedApps$ = applications.map(app =>
        combineLatest([
          from(getDoc(doc(this.firestore, `users/${app.userId}`))),
          from(getDoc(doc(this.firestore, `jobs/${app.jobId}`)))
        ]).pipe(
          map(([userSnap, jobSnap]) => {
            const userData = userSnap.exists() ? (userSnap.data() as any) : {};
            const jobData = jobSnap.exists() ? (jobSnap.data() as any) : {};

            const response: ApplicationResponse = {
              id: app.id || "",
              cvUrl: app.cvUrl,
              coverLetter: app.coverLetter,
              status: app.status,
              applicationDate: app.applicationDate, // Keep as is or convert to Date if needed
              user: {
                id: app.userId,
                fullName: userData.fullName || '',
                email: userData.email || '',
                phone: userData.phoneCode + userData.phoneNumber,
                image: userData.image
              },
              job: {
                id: app.jobId,
                title: jobData.title || '',
                category: jobData.category || ''
              }
            };
            return response;
          })
        )
      );

      return combineLatest(detailedApps$);
    })
  );
}
  // Get all applications by job
  getApplicationsByJob(jobId: string): Observable<Application[]> {
    const q = query(this.applicationsCollection, where('jobId', '==', jobId));
    return collectionData(q, { idField: 'id' }) as Observable<Application[]>;
  }

  // Update application status
  updateApplicationStatus(applicationId: string, newStatus: 'pending' | 'reviewed' | 'accepted' | 'rejected'): Observable<void> {
    if (!['pending', 'reviewed', 'accepted', 'rejected'].includes(newStatus)) {
      return new Observable(observer => observer.error(new Error('Invalid status value')));
    }
    const docRef = doc(this.firestore, `applications/${applicationId}`);
    return from(updateDoc(docRef, { status: newStatus }));
  }

  // Get all applications with user & job details
 getAllApplicationsWithDetails(): Observable<ApplicationResponse[]> {
  // Apply orderBy to the Firestore query
  const applicationsQuery = query(this.applicationsCollection, orderBy('applicationDate', 'desc'));

  return (collectionData(applicationsQuery, { idField: 'id' }) as Observable<Application[]>).pipe(
    switchMap((applications: Application[]) => {
      const detailedApps$ = applications.map(app =>
        combineLatest([
          from(getDoc(doc(this.firestore, `users/${app.userId}`))),
          from(getDoc(doc(this.firestore, `jobs/${app.jobId}`)))
        ]).pipe(
          map(([userSnap, jobSnap]) => {
            const userData = userSnap.exists() ? (userSnap.data() as any) : {};
            const jobData = jobSnap.exists() ? (jobSnap.data() as any) : {};

            const response: ApplicationResponse = {
              id: app.id || "",
              cvUrl: app.cvUrl,
              coverLetter: app.coverLetter,
              status: app.status,
              applicationDate: app.applicationDate, // Keep as is or convert to Date if needed
              user: {
                id: app.userId,
                fullName: userData.fullName || '',
                email: userData.email || '',
                phone: userData.phoneCode + userData.phoneNumber,
                image: userData.image
              },
              job: {
                id: app.jobId,
                title: jobData.title || '',
                category: jobData.category || ''
              }
            };
            return response;
          })
        )
      );

      return combineLatest(detailedApps$);
    })
  );
}
  // Get a single application by ID with user and job details
  getApplicationById(applicationId: string): Observable<ApplicationResponse> {
    const docRef = doc(this.firestore, `applications/${applicationId}`);
    return from(getDoc(docRef)).pipe(
      switchMap(appSnap => {
        if (!appSnap.exists()) {
          throw new Error('Application not found');
        }
        const app = { id: appSnap.id, ...appSnap.data() } as Application;
        return combineLatest([
          from(getDoc(doc(this.firestore, `users/${app.userId}`))),
          from(getDoc(doc(this.firestore, `jobs/${app.jobId}`)))
        ]).pipe(
          map(([userSnap, jobSnap]) => {
            const userData = userSnap.exists() ? (userSnap.data() as any) : {};
            const jobData = jobSnap.exists() ? (jobSnap.data() as any) : {};

            const response: ApplicationResponse = {
              id: app.id || "",
              cvUrl: app.cvUrl,
              coverLetter: app.coverLetter,
              status: app.status,
              applicationDate: app.applicationDate,
              user: {
                id: app.userId,
                fullName: userData.fullName || '',
                email: userData.email || '',
                phone: userData.phoneCode+userData.phoneNumber,
                image: userData.image
              },
              job: {
                id: app.jobId,
                title: jobData.title || '',
                category: jobData.category || ''
              }
            };
            return response;
          })
        );
      })
    );
  }

  // Delete application
  deleteApplication(applicationId: string): Observable<void> {
    const docRef = doc(this.firestore, `applications/${applicationId}`);
    return from(deleteDoc(docRef));
  }
}